import { Text } from '@thanx/uikit/text'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

export type TimelineItem = {
  // Stable composite key (index + timestamp + label) built by the caller —
  // collision-proof even when the backend repeats a (timestamp, label) pair.
  id: string
  time: string
  description: string
}

type Props = {
  items: TimelineItem[]
  // Accessible name for the list. Supplied by each caller from its OWN locale
  // bundle — this shared component must not couple to a scene's namespace.
  ariaLabel: string
}

// Vertical stepper (AI-38 Figma "Case timeline"): a blue dot per event with a
// thin connector line bridging each dot to the next. The connector lives in a
// flex column beneath the dot so it stretches to fill the row height; the last
// item omits it so the chain terminates on its final dot.
const Timeline = ({ items, ariaLabel }: Props) => {
  const [css] = useStyletron()

  return (
    <ol
      aria-label={ariaLabel}
      className={css({
        listStyle: 'none',
        padding: 0,
        margin: 0,
      })}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1

        return (
          <li
            key={item.id}
            className={css({
              display: 'flex',
              alignItems: 'stretch',
            })}
          >
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '16px',
                flexShrink: 0,
              })}
            >
              <div
                className={css({
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: merchantTheme.colors.belizeHole50,
                  flexShrink: 0,
                })}
                aria-hidden="true"
              />
              {!isLast && (
                <div
                  className={css({
                    width: '1px',
                    flex: 1,
                    backgroundColor: merchantTheme.colors.grey20,
                    // Bottom margin stops the line a little short of the next
                    // dot (per the mockup), rather than running into it.
                    margin: '2px auto 8px',
                  })}
                  aria-hidden="true"
                />
              )}
            </div>
            {/* The inter-step gap lives on the text column (inside the flex
                stretch area), NOT the <li> padding — so the marker column
                stretches through it and the connector line runs the full way
                down to the next dot rather than stopping at the text. */}
            <div
              className={css({
                marginLeft: '12px',
                paddingBottom: isLast ? '0' : '24px',
              })}
            >
              <Text.BodyText4 bold tag="div">
                {item.time}
              </Text.BodyText4>
              <Text.BodyText4 color="grey70" tag="div">
                {item.description}
              </Text.BodyText4>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export default Timeline
